import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { validatePassword, getStrengthColor, getStrengthText } from '@/utils/passwordValidation';
import { sanitizeAndValidateFormData, sanitizeInput } from '@/utils/inputSanitization';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import Navbar from '@/components/Navbar';

const Auth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [resetLoading, setResetLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({ email: '', password: '', fullName: '', confirmPassword: '', personType: 'pf', companyName: '', cpf: '', cnpj: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const formatCPF = (v: string) => {
    let d = v.replace(/\D/g, '').slice(0, 11);
    d = d.replace(/(\d{3})(\d)/, '$1.$2')
         .replace(/(\d{3})(\d)/, '$1.$2')
         .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    return d;
  };

  const formatCNPJ = (v: string) => {
    let d = v.replace(/\D/g, '').slice(0, 14);
    d = d.replace(/(\d{2})(\d)/, '$1.$2')
         .replace(/(\d{3})(\d)/, '$1.$2')
         .replace(/(\d{3})(\d)/, '$1/$2')
         .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    return d;
  };

  // Função para verificar admin e redirecionar
  const checkAdminAndRedirect = async (userId: string) => {
    try {
      const { data } = await supabase.rpc('is_admin', { user_id: userId });
      if (data === true) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch {
      navigate('/dashboard');
    }
  };

  useEffect(() => {
    if (user) {
      checkAdminAndRedirect(user.id);
    }
  }, [user, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setFormErrors({});

    try {
      // Validate and sanitize input
      const { sanitized, errors, isValid } = sanitizeAndValidateFormData(loginData);

      if (!isValid) {
        setFormErrors(errors);
        setIsLoading(false);
        return;
      }

      const { error } = await signIn(sanitized.email, sanitized.password);

      if (error) {
        toast({
          title: "Erro no login",
          description: `Erro: ${error.message}`,
          variant: "destructive",
        });
      } else {
        // Após login, verificar se é admin e redirecionar
        const { data: { user: loggedUser } } = await supabase.auth.getUser();
        if (loggedUser) {
          await checkAdminAndRedirect(loggedUser.id);
        }
      }
    } catch {
      toast({
        title: "Erro de conexão",
        description: "Verifique sua conexão com a internet e tente novamente.",
        variant: "destructive",
      });
    }

    setIsLoading(false);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setFormErrors({});

    // Validate and sanitize only required input fields
    const requiredFields = {
      email: signupData.email,
      password: signupData.password,
      confirmPassword: signupData.confirmPassword,
      fullName: signupData.fullName,
    };

    const { sanitized, errors, isValid } = sanitizeAndValidateFormData(requiredFields);

    if (!isValid) {
      setFormErrors(errors);
      setIsLoading(false);
      return;
    }

    // Password confirmation check
    if (sanitized.password !== sanitized.confirmPassword) {
      setFormErrors({ confirmPassword: 'Senhas não conferem' });
      setIsLoading(false);
      return;
    }

    // Password strength validation
    const passwordValidation = validatePassword(sanitized.password);
    if (!passwordValidation.isValid) {
      setFormErrors({ password: passwordValidation.errors[0] });
      setIsLoading(false);
      return;
    }

    const cpfDigits = (signupData.cpf || '').replace(/\D/g, '');
    const cnpjDigits = (signupData.cnpj || '').replace(/\D/g, '');

    try {
      const { error } = await signUp(
        sanitized.email,
        sanitized.password,
        sanitized.fullName,
        {
          personType: signupData.personType as 'pf' | 'pj',
          companyName: sanitizeInput(signupData.companyName || ''),
          cpf: cpfDigits || undefined,
          cnpj: cnpjDigits || undefined,
        }
      );

      if (error) {
        let errorMessage = error.message;
        const m = (error.message || '').toLowerCase();

        // Traduzir mensagens comuns de erro (ordem importa!)
        if (
          error.message.includes('já está cadastrado') ||
          error.message.includes('already registered') ||
          error.message.includes('User already registered')
        ) {
          errorMessage = 'Este email já está cadastrado. Tente fazer login ou recuperar sua senha.';
        } else if (
          m.includes('confirmation email') ||
          m.includes('recovery email') ||
          m.includes('could not send email') ||
          m.includes('associated domain') ||
          m.includes('not verified')
        ) {
          errorMessage = 'Falha ao enviar o email de confirmação. Isso geralmente é configuração de envio de emails (SMTP) no Supabase. Ajuste o SMTP para um domínio verificado (ex: ai4mebrasil.com) ou desative a confirmação de email para testes.';
        } else if (m.includes('password')) {
          errorMessage = 'Senha inválida. Use pelo menos 8 caracteres, incluindo números e letras.';
        } else if (m.includes('invalid email')) {
          // Só mostrar "email inválido" para erros específicos de formato
          errorMessage = 'Email inválido. Verifique o formato do email.';
        }

        toast({
          title: "Erro no cadastro",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Cadastro realizado com sucesso!",
          description: "Verifique seu email para confirmar sua conta e depois faça o login.",
        });
        // Limpar formulário e voltar para aba de login
        setSignupData({
          email: '',
          password: '',
          fullName: '',
          confirmPassword: '',
          personType: 'pf',
          companyName: '',
          cpf: '',
          cnpj: '',
        });
        setActiveTab('login');
      }
    } catch {
      toast({
        title: "Erro de conexão",
        description: "Verifique sua conexão com a internet e tente novamente.",
        variant: "destructive",
      });
    }

    setIsLoading(false);
  };


  const handlePasswordReset = async () => {
    if (!loginData.email) {
      toast({
        title: "Email necessário",
        description: "Digite seu email no campo acima para redefinir a senha.",
        variant: "destructive",
      });
      return;
    }

    setResetLoading(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(
        loginData.email,
        {
          redirectTo: 'https://www.ai4me.com.br/redefinir-senha',
        }
      );

      if (error) {
        let errorMessage = error.message;
        
        // Tratamento específico para rate limiting
        if (error.message.includes("Too many requests") || error.message.includes("rate limit")) {
          errorMessage = "Muitas tentativas de redefinição de senha. Aguarde alguns minutos antes de tentar novamente.";
        } else if (error.message.includes("Email rate limit exceeded")) {
          errorMessage = "Limite de emails excedido. Aguarde antes de solicitar novamente.";
        } else if (error.message.includes("For security purposes")) {
          errorMessage = "Por motivos de segurança, aguarde alguns minutos antes de solicitar outro email de redefinição.";
        }
        
        toast({
          title: "Erro ao enviar email",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Email enviado!",
          description: "Verifique seu email para redefinir sua senha. Se não receber em alguns minutos, verifique a pasta de spam.",
        });
      }
    } catch (err: any) {
      let errorMessage = "Verifique sua conexão com a internet e tente novamente.";
      
      if (err.message?.includes("rate") || err.message?.includes("limit")) {
        errorMessage = "Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.";
      }
      
      toast({
        title: "Erro de conexão",
        description: errorMessage,
        variant: "destructive",
      });
    }
    
    setResetLoading(false);
  };
  
  // Real-time password validation
  const passwordValidation = validatePassword(signupData.password);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5">
      <Navbar />
      <div className="flex items-center justify-center p-4 pt-24">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/lovable-uploads/a90037e4-0e6f-4f0a-85cd-f03382deeb41.png" 
              alt="AI4ME - logomarca" 
              className="h-12 w-auto"
            />
          </div>
          <CardDescription>IA na prática para transformar o seu negócio e impulsionar seus resultados.</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="signup">Cadastro</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                    required
                    className={formErrors.email ? 'border-destructive' : ''}
                  />
                  {formErrors.email && (
                    <p className="text-sm text-destructive">{formErrors.email}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={loginData.password}
                      onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                      required
                      className={formErrors.password ? 'border-destructive pr-10' : 'pr-10'}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {formErrors.password && (
                    <p className="text-sm text-destructive">{formErrors.password}</p>
                  )}
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Entrar
                </Button>
                
                
                <div className="text-center">
                  <Button 
                    type="button" 
                    variant="link" 
                    onClick={handlePasswordReset}
                    disabled={resetLoading}
                    className="text-sm"
                  >
                    {resetLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Esqueci minha senha
                  </Button>
                </div>
              </form>
            </TabsContent>
            
            <TabsContent value="signup">
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">Nome Completo</Label>
                  <Input
                    id="signup-name"
                    type="text"
                    value={signupData.fullName}
                    onChange={(e) => setSignupData(prev => ({ ...prev, fullName: e.target.value }))}
                    required
                    className={formErrors.fullName ? 'border-destructive' : ''}
                  />
                  {formErrors.fullName && (
                    <p className="text-sm text-destructive">{formErrors.fullName}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    value={signupData.email}
                    onChange={(e) => setSignupData(prev => ({ ...prev, email: e.target.value }))}
                    required
                    className={formErrors.email ? 'border-destructive' : ''}
                  />
                  {formErrors.email && (
                    <p className="text-sm text-destructive">{formErrors.email}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Senha</Label>
                  <div className="relative">
                    <Input
                      id="signup-password"
                      type={showPassword ? "text" : "password"}
                      value={signupData.password}
                      onChange={(e) => setSignupData(prev => ({ ...prev, password: e.target.value }))}
                      required
                      className={formErrors.password ? 'border-destructive pr-10' : 'pr-10'}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {signupData.password && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span>Força da senha:</span>
                        <span className={getStrengthColor(passwordValidation.strength)}>
                          {getStrengthText(passwordValidation.strength)}
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full transition-all ${
                            passwordValidation.strength === 'strong' ? 'bg-green-500' :
                            passwordValidation.strength === 'medium' ? 'bg-yellow-500' : 'bg-destructive'
                          }`}
                          style={{ 
                            width: `${
                              passwordValidation.strength === 'strong' ? 100 :
                              passwordValidation.strength === 'medium' ? 60 : 30
                            }%` 
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {formErrors.password && (
                    <p className="text-sm text-destructive">{formErrors.password}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirmar Senha</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      value={signupData.confirmPassword}
                      onChange={(e) => setSignupData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                      className={formErrors.confirmPassword ? 'border-destructive pr-10' : 'pr-10'}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {formErrors.confirmPassword && (
                    <p className="text-sm text-destructive">{formErrors.confirmPassword}</p>
                  )}
                </div>
                {/* Tipo de perfil (opcional) */}
                <div className="space-y-2">
                  <Label>Tipo de perfil (opcional)</Label>
                  <RadioGroup
                    className="grid grid-cols-2 gap-4"
                    value={signupData.personType}
                    onValueChange={(v) => setSignupData(prev => ({ ...prev, personType: v as 'pf' | 'pj' }))}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem id="tipo-pf" value="pf" />
                      <Label htmlFor="tipo-pf" className="font-normal">Pessoa Física</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem id="tipo-pj" value="pj" />
                      <Label htmlFor="tipo-pj" className="font-normal">Pessoa Jurídica</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Campos dinâmicos conforme o tipo */}
                {signupData.personType === 'pj' ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="companyName">Nome da empresa (opcional)</Label>
                      <Input
                        id="companyName"
                        type="text"
                        value={signupData.companyName}
                        onChange={(e) => setSignupData(prev => ({ ...prev, companyName: e.target.value }))}
                        placeholder="Ex: Minha Empresa LTDA"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cnpj">CNPJ (opcional)</Label>
                      <Input
                        id="cnpj"
                        type="text"
                        value={signupData.cnpj}
                        onChange={(e) => setSignupData(prev => ({ ...prev, cnpj: formatCNPJ(e.target.value) }))}
                        placeholder="00.000.000/0000-00"
                      />
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="cpf">CPF (opcional)</Label>
                    <Input
                      id="cpf"
                      type="text"
                      value={signupData.cpf}
                      onChange={(e) => setSignupData(prev => ({ ...prev, cpf: formatCPF(e.target.value) }))}
                      placeholder="000.000.000-00"
                    />
                  </div>
                )}

                {/* Checkbox LGPD - Obrigatório */}
                <div className="space-y-2">
                  <div className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      id="accept-terms"
                      required
                      className="mt-1"
                    />
                    <Label htmlFor="accept-terms" className="text-sm leading-relaxed">
                      Aceito os{' '}
                      <Button
                        type="button"
                        variant="link"
                        className="h-auto p-0 text-sm underline"
                        onClick={() => window.open('/termos-de-uso', '_blank')}
                      >
                        Termos de Uso
                      </Button>
                      {' '}e a{' '}
                      <Button
                        type="button"
                        variant="link"
                        className="h-auto p-0 text-sm underline"
                        onClick={() => window.open('/lgpd', '_blank')}
                      >
                        Política de Privacidade
                      </Button>
                    </Label>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Cadastrar
                </Button>
                
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default Auth;